import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  ApiKeyCredentialIntegrationType,
  interpolateRoute,
  type DoorLoopAssignee,
  type DoorLoopMetadata,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

/**
 * DoorLoop Integration API Client
 * DoorLoop uses a single bearer API key against a fixed base URL. Connect goes
 * through a dedicated endpoint that validates the key (role + Premium access)
 * before storing it.
 */
export class DoorLoopClient extends BaseApiClient {
  /**
   * Connect DoorLoop by validating and storing the API key.
   * Surfaces a 400 (invalid key / insufficient role / non-Premium) to the caller.
   */
  async connect(apiKey: string): Promise<void> {
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          API_ROUTES.INTEGRATIONS.DOORLOOP.CONNECT,
          {
            method: 'POST',
            body: JSON.stringify({ apiKey }),
          },
        )
      },
      { errorContext: 'Error connecting DoorLoop integration' },
    )
  }

  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          API_ROUTES.INTEGRATIONS.DOORLOOP.DISCONNECT,
          { method: 'DELETE' },
        )
      },
      { errorContext: 'Error disconnecting DoorLoop integration' },
    )
  }

  /**
   * [ADMIN] Connect DoorLoop for a specific company (super-admin flow).
   * Validates the key server-side before storing; surfaces a 400 on failure.
   */
  async connectAdmin(companyId: string, apiKey: string): Promise<void> {
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${API_ROUTES.ADMIN.INTEGRATIONS.DOORLOOP.CONNECT}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ apiKey }),
          },
        )
      },
      { errorContext: 'Error connecting admin DoorLoop integration' },
    )
  }

  /**
   * Update DoorLoop integration metadata (task push defaults).
   * Uses the generic API key credentials update endpoint with metadata only.
   */
  async updateMetadata(metadata: DoorLoopMetadata): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.DOORLOOP },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ metadata }),
        })
      },
      { errorContext: 'Error updating DoorLoop metadata' },
    )
  }

  /**
   * [ADMIN] Update DoorLoop integration metadata for a specific company.
   */
  async updateAdminMetadata(
    companyId: string,
    metadata: DoorLoopMetadata,
  ): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.DOORLOOP },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          { method: 'PUT', body: JSON.stringify({ metadata }) },
        )
      },
      { errorContext: 'Error updating admin DoorLoop metadata' },
    )
  }

  /** Shared GET for the users/vendors option lists (regular + admin routes). */
  private async fetchOptions(
    url: string,
    errorContext: string,
  ): Promise<DoorLoopAssignee[]> {
    const result = await withErrorHandling(
      async () => {
        const response =
          await this.request<ApiResponse<DoorLoopAssignee[]>>(url)
        return response.data ?? []
      },
      { errorContext },
    )
    return result ?? []
  }

  /**
   * Get DoorLoop users (task assignees/requesters) for the current user's company
   */
  async getUsers(): Promise<DoorLoopAssignee[]> {
    return this.fetchOptions(
      API_ROUTES.INTEGRATIONS.DOORLOOP.USERS,
      'Error fetching DoorLoop users',
    )
  }

  /**
   * [ADMIN] Get DoorLoop users (task assignees/requesters) for a specific company
   */
  async getAdminUsers(companyId: string): Promise<DoorLoopAssignee[]> {
    return this.fetchOptions(
      `${API_ROUTES.ADMIN.INTEGRATIONS.DOORLOOP.USERS}?companyId=${companyId}`,
      'Error fetching admin DoorLoop users',
    )
  }

  /**
   * Get DoorLoop vendors (work-order assignees) for the current user's company
   */
  async getVendors(): Promise<DoorLoopAssignee[]> {
    return this.fetchOptions(
      API_ROUTES.INTEGRATIONS.DOORLOOP.VENDORS,
      'Error fetching DoorLoop vendors',
    )
  }

  /**
   * [ADMIN] Get DoorLoop vendors (work-order assignees) for a specific company
   */
  async getAdminVendors(companyId: string): Promise<DoorLoopAssignee[]> {
    return this.fetchOptions(
      `${API_ROUTES.ADMIN.INTEGRATIONS.DOORLOOP.VENDORS}?companyId=${companyId}`,
      'Error fetching admin DoorLoop vendors',
    )
  }

  /** [ADMIN] Disconnect DoorLoop for a specific company (super-admin flow). */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.DOORLOOP },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          { method: 'DELETE' },
        )
      },
      { errorContext: 'Error disconnecting admin DoorLoop integration' },
    )
  }
}

export const doorLoopClient = new DoorLoopClient()
